import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, takeUntil, withLatestFrom } from 'rxjs';
import { ApiService } from 'src/app/services/api-services/api.service';
import { CalendarService } from 'src/app/services/calendar.service';
import {
  Appreciate,
  setAppreciate,
} from 'src/app/state/appreciate/appreciate.actions';
import {
  selectOrgSettings,
  selectOrgSettingsSuccess,
} from 'src/app/store/auth/auth.selector';
import { TokenAuthState } from 'src/app/store/auth/auth.state';
import { selectCartCount } from 'src/app/store/cart/cart.selectors';
import { AutoUnsubscribeComponent } from 'src/app/utility/auto-unsubscribe.component';
import {
  formatNavArrayItems,
  getLabelForMenuItem,
  isAppreciateEnabled,
  isBenefitReceiptEnabled,
  isCalendarEnabled,
  isEventsAnnouncementEnabled,
  isLeaderboardEnabled,
  isNominationEnabled,
  isPostPollEnabled,
  isQuickLinkEnabled,
  isRewardEnabled,
  isSalesReceiptEnabled,
  navItemRedirectionLink,
} from 'src/app/utility/common-logic';
import { FeedType } from 'src/app/utility/enum';
import { OrganizationSettings } from 'src/app/utility/model/organization';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-bottom-menu',
  templateUrl: './bottom-menu.component.html',
  styleUrls: ['./bottom-menu.component.scss'],
})
export class BottomMenuComponent
  extends AutoUnsubscribeComponent
  implements OnInit
{
  modules = environment.settings.modules;
  showActive = environment.settings.showActiveDotInBottomMenu;
  cartDetails: any;
  cartCount: number;
  to: string = 'home';
  moreMenu = inject(NgbModal);
  moreButtonHidden: boolean = environment.settings.showMoreButtonInBottomMenu;
  flexiBenefitLink = '';
  code: string = '';
  isShowAnnouncementEvents = environment.settings.isShowAnnouncementEvents;
  showAnnouncementsExplicitly =
    environment.settings.showAnnouncementsExplicitly;
  showSettingsInMenu = environment.settings.showSettingsInMenu;
  navMenuItems = [];
  orderOfMenuItems = environment.settings.orderOfMenuItems;
  auth$: TokenAuthState;
  orgSettings: OrganizationSettings;
  showQuiz = environment.settings?.isShowQuiz;
  showQuest = environment.settings?.isShowQuest;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private appreciateStore: Store<{ appreciate: Appreciate }>,
    private translate: TranslateService,
    private api: ApiService,
    private store: Store,
    private cal: CalendarService,
  ) {
    super();
  }

  async ngOnInit() {
    this.activatedRoute.url.subscribe((url: UrlSegment[]) => {
      this.to = url.length > 0 ? url[0].path : this.router.url.split('/')[1];
    });
    if (this.modules.cart) {
      this.store
        .pipe(select(selectCartCount), takeUntil(this.destroy$))
        .subscribe((count) => {
          this.cartCount = count;
        });
    }

    this.store
      .pipe(
        select(selectOrgSettingsSuccess),
        filter((success) => success),
        withLatestFrom(this.store.pipe(select(selectOrgSettings))),
        takeUntil(this.destroy$),
      )
      .subscribe(([_, settings]) => {
        this.orgSettings = settings;
        let updatedNavMenuItems: string[] = [];
        if (this.modules.home) {
          updatedNavMenuItems.push('home');
        }

        if (this.showSettingsInMenu) {
          updatedNavMenuItems.push('settings');
        }

        if (isRewardEnabled(this.orgSettings) && this.modules.rewards) {
          updatedNavMenuItems.push('rewards');
        }

        if (this.modules.myWallet) {
          updatedNavMenuItems.push('my-wallet');
        }

        if (this.modules.pointsHistory) {
          updatedNavMenuItems.push('point-history');
        }

        if (
          (isAppreciateEnabled(this.orgSettings) &&
            isNominationEnabled(this.orgSettings)) ||
          isAppreciateEnabled(this.orgSettings)
        ) {
          updatedNavMenuItems.push('appreciate');
        } else if (isNominationEnabled(this.orgSettings)) {
          updatedNavMenuItems.push('nomination');
        }

        if (
          isAppreciateEnabled(this.orgSettings) &&
          updatedNavMenuItems.includes('nomination')
        ) {
          updatedNavMenuItems = updatedNavMenuItems.filter(
            (x) => x !== 'nomination',
          );
        }

        if (
          isAppreciateEnabled(this.orgSettings) ||
          isNominationEnabled(this.orgSettings) ||
          isPostPollEnabled(this.orgSettings)
        ) {
          updatedNavMenuItems.push('feeds');
        }

        if (
          isCalendarEnabled(this.orgSettings) &&
          !this.isShowAnnouncementEvents
        ) {
          updatedNavMenuItems.push('calendar');
        }

        if (
          !isCalendarEnabled(this.orgSettings) ||
          this.isShowAnnouncementEvents
        ) {
          updatedNavMenuItems = updatedNavMenuItems.filter(
            (x) => x !== 'calendar',
          );
        }

        if (
          (isCalendarEnabled(this.orgSettings) ||
            isEventsAnnouncementEnabled(this.orgSettings)) &&
          this.showAnnouncementsExplicitly
        ) {
          updatedNavMenuItems.push('announcement');
        }

        if (isQuickLinkEnabled(this.orgSettings)) {
          updatedNavMenuItems.push('quicklink');
        }

        if (
          isEventsAnnouncementEnabled(this.orgSettings) &&
          this.isShowAnnouncementEvents
        ) {
          updatedNavMenuItems.push('announcement', 'event');
        }

        if (
          isBenefitReceiptEnabled(this.orgSettings) &&
          !isSalesReceiptEnabled(this.orgSettings)
        ) {
          updatedNavMenuItems.push('receipts');
        }

        if (isSalesReceiptEnabled(this.orgSettings)) {
          updatedNavMenuItems.push('sales-receipts');
        }

        if (isLeaderboardEnabled(this.orgSettings)) {
          updatedNavMenuItems.push('leaderboard');
        }

        if (this.showQuiz) {
          updatedNavMenuItems.push('quiz');
        }
        if (this.showQuest) {
          updatedNavMenuItems.push('quest');
        }

        if (this.orgSettings?.flexi_benefits_url) {
          updatedNavMenuItems.push('flexiBenefits');
        }

        this.navMenuItems = formatNavArrayItems(
          this.orderOfMenuItems,
          updatedNavMenuItems,
        );
      });
  }

  is(to: string) {
    return (
      (to === 'my-wallet' && to === this.router.url.split('/')[2]) ||
      (to === 'appreciate' && to === this.router.url.split('/')[1]) ||
      (to === 'receipts' && this.router.url.split('/')[1] === 'receipt-list') ||
      (to === 'sales-receipts' &&
        this.router.url.split('/')[1] === 'sales-receipt-list') ||
      (to === 'announcement' && to === this.router.url.split('/')[2]) ||
      (to === 'event' && to === this.router.url.split('/')[2]) ||
      (to === 'calendar' &&
        to === this.router.url.split('/')[1] &&
        this.router.url.split('/')[2] === 'all') ||
      this.to === to
    );
  }

  selectedClass(to: string) {
    return this.is(to) ? 'selected' : '';
  }

  fontClass(to: string) {
    if (
      (this.to == 'calendar' &&
        (this.router.url.includes('announcement') ||
          this.router.url.includes('event'))) ||
      this.to == 'announcement' ||
      this.to == 'event'
    ) {
      const selectedUrlTab = this.router.url.split('/')[2];
      this.to = selectedUrlTab;
      return selectedUrlTab === to ? 'font-selected' : 'font-unselected';
    } else {
      return this.to === to ? 'font-selected' : 'font-unselected';
    }
  }

  openModal(content) {
    this.moreMenu.open(content, {
      windowClass: 'modal-bottom-right',
      backdrop: true,
    });
  }

  get bottomMenus() {
    if (this.navMenuItems && this.navMenuItems.length <= 5) {
      return this.navMenuItems;
    } else if (this.navMenuItems && this.navMenuItems.length > 5) {
      return this.navMenuItems.slice(0, 4);
    } else {
      return [];
    }
  }

  get moreMenus() {
    return this.navMenuItems.slice(4);
  }

  get link() {
    if (this.code === 'flexiBenefits') {
      this.flexiBenefitLink = this.orgSettings?.flexi_benefits_url;
    }
    return navItemRedirectionLink(this.code, this.orgSettings);
  }

  label(code: string) {
    return getLabelForMenuItem(code, this.translate, this.orgSettings);
  }

  async redirectTo(link) {
    this.code = link;
    if (this.link[0].startsWith('https')) {
      if (this.link[0] === this.flexiBenefitLink) {
        this.getDeepLinkToken();
      } else {
        window.open(this.link[0], '_blank');
      }
    } else {
      if (link === 'nomination') {
        this.appreciateStore.dispatch(
          setAppreciate({ mode: FeedType.nomination }),
        );
        await this.router.navigate(['/appreciate/nom-cat']);
      } else {
        await this.router.navigate(this.link);
      }
    }
  }

  getDeepLinkToken() {
    this.api.getDeepLinkToken().then((res) => {
      if (res?.deeplink_token) {
        window.open(
          this.flexiBenefitLink + '/auto-login?token=' + res.deeplink_token,
          '_blank',
        );
      }
    });
  }
}
